import { cx } from "class-variance-authority";
import Link from "next/link";
import { useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { ChevronDownIcon } from "@/assets/icons/general/ChevronDown";
import { UserAvatar } from "@/components/shared/layout/UserAvatar";
import { Dropdown } from "@/components/ui/overlays/Dropdown/Dropdown";
import { Typography } from "@/components/ui/text/Typography/Typography";
import { RouteConfig } from "@/config/route.config";
import { AuthContext } from "@/data/auth/auth.context";
import type { CommonModels } from "@/data/common/common.models";

export const UserDropdown = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { isAdmin, user, logout } = AuthContext.useAuth();
  const name = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`;

  return (
    <div className="border-elevation-outline-1 border-t py-2 pl-5 text-text-default-primary">
      <Dropdown
        useTriggerWidth={false}
        trigger={
          <div className="flex cursor-pointer items-center gap-2">
            <UserPreview
              title={user?.organization?.name ?? ""}
              description={name}
              user={user as CommonModels.BizUserDto}
            />
            <ChevronDownIcon
              className={cx("h-5 w-5 text-elevation-outline-2 transition-transform", isOpen && "rotate-180")}
              aria-hidden="true"
            />
          </div>
        }
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        popoverProps={{ crossOffset: 50 }}
        data-testid="user-dropdown"
      >
        <div className="flex flex-col gap-2-5">
          <UserPreview
            title={name}
            description={user?.jobTitle ?? ""}
            user={user as CommonModels.BizUserDto}
          />
          <div className="flex flex-col gap-1">
            <Link
              href={RouteConfig.profileSettings}
              onClick={() => setIsOpen(false)}
              className="py-2"
            >
              <Typography
                size="body-4"
                className="text-text-default-tertiary"
              >
                {t("ui.userDropdown.profileSettings")}
              </Typography>
            </Link>
            {isAdmin && (
              <Link
                href={RouteConfig.organizationSettings}
                onClick={() => setIsOpen(false)}
                className="py-2"
              >
                <Typography
                  size="body-4"
                  className="text-text-default-tertiary"
                >
                  {t("ui.userDropdown.organizationSettings")}
                </Typography>
              </Link>
            )}
          </div>
          <div className="w-full border border-elevation-outline-1" />

          <Link
            href={`${RouteConfig.bugReport}?source=${encodeURIComponent(window.location.href)}`}
            onClick={() => setIsOpen(false)}
            className="py-2"
          >
            <Typography
              size="body-4"
              className="text-text-default-tertiary"
            >
              {t("ui.userDropdown.reportABug")}
            </Typography>
          </Link>
          <AriaButton
            className="flex w-full justify-start py-2 outline-none"
            data-testid="logout-button"
            onPress={async () => {
              setIsOpen(false);
              await logout();
            }}
          >
            <Typography
              size="body-4"
              className="text-text-default-tertiary"
            >
              {t("ui.userDropdown.signOut", { orgName: user?.organization?.name })}
            </Typography>
          </AriaButton>
        </div>
      </Dropdown>
    </div>
  );
};

const UserPreview = ({
  title,
  description,
  user,
}: {
  title: string;
  description: string;
  user?: CommonModels.BizUserDto;
}) => (
  <div className="flex w-full items-center gap-3">
    <UserAvatar user={user} />
    <div className="flex flex-col items-start justify-start overflow-hidden">
      <Typography
        size="label-3"
        variant="prominent-2"
        className="w-full truncate text-left"
      >
        {title}
      </Typography>
      <Typography
        size="label-4"
        className="w-full truncate text-left text-text-default-tertiary"
      >
        {description}
      </Typography>
    </div>
  </div>
);
